import { ref, onUnmounted } from 'vue'
import type {
  WebSocketEvent,
  MessageEventData,
  MessageStatusEventData,
  UserSendTemplateMessageEvent,
  SendMessagePayload,
  EnterChatPayload,
  ExitChatPayload,
  EndChatPayload,
  ReadChatPayload,
  SendFilePayload,
  InteractionMessagePayload,
  SendTemplatePayload,
  TransferUserPayload,
  ConfirmTransferPayload,
  NewChatEvent,
  NewChatTemplateEvent,
  UserEntryChatEvent,
  UserTookChatEvent,
  UserExitChatEvent,
  InteractionEvent,
  TransferRequestEvent,
  TransferAcceptedEvent,
  ChatTransferredToCampaignEvent,
  ChatTransferredToBotEvent,
  ChatFinishedEvent,
  ChatUpdatedEvent,
} from '../types/interfaces'
import { WebSocketEventName, UserRole } from '../types/enums'
import {
  WHATSAPP_CONFIG,
  SESSION_REPLACED_CLOSE_CODE,
  SESSION_EXPIRED_CLOSE_CODE,
  UNAUTHORIZED_CLOSE_CODE,
} from './constants'
import { getWebSocketUrl } from '@/helpers/api-url.helper'
import { useConnectivityStore } from '@/stores/useConnectivityStore'

export const useWhatsAppService = () => {
  const connectivityStore = useConnectivityStore()
  const ws = ref<WebSocket | null>(null)
  const isConnected = ref(false)
  const reconnectAttempts = ref(0)
  const lastCloseCode = ref<number | null>(null)
  const isConnecting = ref(false)
  const isReconnecting = ref(false)
  const currentReconnectDelay = ref(WHATSAPP_CONFIG.RECONNECT_DELAY)
  const maxReconnectAttempts = WHATSAPP_CONFIG.MAX_RECONNECT_ATTEMPTS
  const reconnectDelay = WHATSAPP_CONFIG.RECONNECT_DELAY
  const reconnectTimeout = ref<number | null>(null)
  const heartbeatInterval = ref<number | null>(null)
  const connectionUserId = ref<string | null>(null)
  const connectionUserRole = ref<UserRole>(UserRole.OPERATOR)
  let intentionalDisconnect = false

  const eventHandlers = new Map<string, ((data: unknown) => void)[]>()

  // Cualquier frame del backend (evento, PING o PONG) prueba que el socket sigue
  // vivo. Es lo que resuelve las sondas que lanza probeConnection().
  const pendingProbes: ((isAlive: boolean) => void)[] = []

  const settlePendingProbes = (isAlive: boolean) => {
    while (pendingProbes.length) pendingProbes.shift()?.(isAlive)
  }

  const clearHeartbeat = () => {
    if (heartbeatInterval.value !== null) {
      clearInterval(heartbeatInterval.value)
      heartbeatInterval.value = null
    }
  }

  const sendPong = () => {
    if (!connectionUserId.value) {
      return
    }

    send({
      event: 'PONG',
      data: {
        user_id: connectionUserId.value,
      },
    })
  }

  const startHeartbeat = () => {
    clearHeartbeat()
    heartbeatInterval.value = setInterval(() => {
      sendPong()
    }, WHATSAPP_CONFIG.PONG_INTERVAL_MS) as unknown as number
  }

  const connect = (userId: string, role: UserRole = UserRole.OPERATOR): Promise<void> => {
    return new Promise((resolve, reject) => {
      try {
        if (ws.value?.readyState === WebSocket.OPEN) {
          resolve()
          return
        }

        if (isConnecting.value) {
          resolve()
          return
        }

        if (ws.value) {
          clearHeartbeat()
          intentionalDisconnect = true
          ws.value.onopen = null
          ws.value.onclose = null
          ws.value.onerror = null
          ws.value.onmessage = null
          ws.value.close()
          ws.value = null
        }

        // Guardar credenciales para reconexión
        connectionUserId.value = userId
        connectionUserRole.value = role

        isConnecting.value = true
        const wsUrl = getWebSocketUrl('api/oper/whatsapp/ws')
        // El token se lee en cada conexion, como en useApiClient: una
        // reconexion tras un nuevo login tiene que llevar el token nuevo.
        const query = new URLSearchParams({
          id: userId,
          role,
          token: localStorage.getItem('oper_access_token') || '',
        })
        const url = `${wsUrl}?${query}`


        ws.value = new WebSocket(url)
        intentionalDisconnect = false

        ws.value.onopen = () => {
          isConnected.value = true
          connectivityStore.setWebSocketConnected(true)
          isConnecting.value = false
          isReconnecting.value = false
          reconnectAttempts.value = 0
          currentReconnectDelay.value = reconnectDelay
          startHeartbeat()
          sendPong()

          emitConnectionStateChange('connected')
          resolve()
        }

        ws.value.onclose = (event) => {
          settlePendingProbes(false)
          clearHeartbeat()
          isConnected.value = false
          connectivityStore.setWebSocketConnected(intentionalDisconnect || !connectionUserId.value)
          isConnecting.value = false
          lastCloseCode.value = event.code


          // Siempre intentar reconectar si hay credenciales guardadas
          if (!connectionUserId.value) {
            emitConnectionStateChange('closed')
            return
          }

          // La sesion se abrio en otro lado. Reconectar desplazaria a la nueva
          // y volveriamos a recibir este mismo cierre: bucle entre dos
          // navegadores con JWT valido. Se corta aqui y el coordinador se
          // encarga de devolver al agente al login.
          if (event.code === SESSION_REPLACED_CLOSE_CODE) {
            connectionUserId.value = null
            emitConnectionStateChange('session_replaced')
            return
          }

          // La sesion ya esta cerrada en el backend: cada reintento recibiria
          // este mismo cierre.
          if (event.code === SESSION_EXPIRED_CLOSE_CODE) {
            connectionUserId.value = null
            emitConnectionStateChange('session_expired')
            return
          }

          // Token ausente, vencido o de otro usuario: reintentar con el mismo
          // token recibiria el mismo cierre.
          if (event.code === UNAUTHORIZED_CLOSE_CODE) {
            connectionUserId.value = null
            emitConnectionStateChange('unauthorized')
            return
          }


          // Resetear isReconnecting para permitir un nuevo ciclo de reconexión
          // Solo si no hay un timeout programado
          if (reconnectTimeout.value === null) {
            isReconnecting.value = false
          }

          emitConnectionStateChange('disconnected')

          // Iniciar reconexión si no está ya en proceso
          if (!isReconnecting.value && reconnectTimeout.value === null) {
            handleReconnect()
          }
        }

        ws.value.onerror = (error) => {
          settlePendingProbes(false)
          isConnecting.value = false
          connectivityStore.setWebSocketConnected(false)
          emitConnectionStateChange('error')

          // Solo rechazar si es el primer intento de conexión y no estamos reconectando
          if (reconnectAttempts.value === 0 && !isReconnecting.value) {
            reject(error)
          }
        }

        ws.value.onmessage = (event) => {
          settlePendingProbes(true)

          try {
            const parsed = JSON.parse(event.data) as Record<string, unknown>
            const wsControlEvent =
              typeof parsed.event === 'string'
                ? parsed.event.toUpperCase()
                : typeof parsed.type === 'string'
                ? parsed.type.toUpperCase()
                : null

            if (wsControlEvent === 'PING') {
              sendPong()
              return
            }

            if (wsControlEvent === 'PONG') {
              return
            }

            handleIncomingMessage(parsed as unknown as WebSocketEvent)
          } catch (error) {
            console.warn('[WhatsApp WS] Error al parsear mensaje entrante:', error, event.data)
          }
        }
      } catch (error) {
        isConnecting.value = false
        reject(error)
      }
    })
  }

  const handleReconnect = () => {

    // Limpiar timeout anterior si existe
    if (reconnectTimeout.value !== null) {
      clearTimeout(reconnectTimeout.value)
      reconnectTimeout.value = null
    }

    if (!connectionUserId.value) {
      isReconnecting.value = false
      return
    }

    isReconnecting.value = true

    // Reintenta indefinidamente; el contador se congela solo para la UI.
    if (reconnectAttempts.value < maxReconnectAttempts) {
      reconnectAttempts.value++
    }

    // Calcular delay con backoff exponencial ANTES del intento
    const delay = Math.min(
      reconnectDelay * Math.pow(WHATSAPP_CONFIG.RECONNECT_BACKOFF_FACTOR, reconnectAttempts.value - 1),
      WHATSAPP_CONFIG.MAX_RECONNECT_DELAY
    )


    // Actualizar el delay actual para que se refleje en la UI
    currentReconnectDelay.value = delay


    emitConnectionStateChange('reconnecting', {
      attempt: reconnectAttempts.value,
      maxAttempts: maxReconnectAttempts,
      delay
    })

    reconnectTimeout.value = setTimeout(() => {
      reconnectTimeout.value = null
      connect(connectionUserId.value!, connectionUserRole.value)
        .then(() => {
        })
        .catch((error) => {
          // NO llamar recursivamente a handleReconnect aquí
          // El evento onclose del WebSocket se encargará de reintentar si es necesario
          console.warn('[WhatsApp WS] Intento de reconexión falló:', error)
        })
    }, delay) as unknown as number

  }

  const disconnect = () => {
    clearHeartbeat()
    if (reconnectTimeout.value !== null) {
      clearTimeout(reconnectTimeout.value)
      reconnectTimeout.value = null
    }

    isReconnecting.value = false
    isConnecting.value = false
    connectivityStore.setWebSocketConnected(true)
    reconnectAttempts.value = 0
    currentReconnectDelay.value = reconnectDelay

    // Limpiar credenciales para evitar reconexión automática
    connectionUserId.value = null

    if (ws.value) {
      intentionalDisconnect = true
      ws.value.close(1000, 'Desconexión intencional')
      ws.value = null
      isConnected.value = false
      eventHandlers.clear()
    }
  }

  const getConnectionState = (): string => {
    if (!ws.value) return 'DISCONNECTED'

    const states: Record<number, string> = {
      [WebSocket.CONNECTING]: 'CONNECTING',
      [WebSocket.OPEN]: 'OPEN',
      [WebSocket.CLOSING]: 'CLOSING',
      [WebSocket.CLOSED]: 'CLOSED',
    }

    return states[ws.value.readyState] || 'UNKNOWN'
  }

  const handleIncomingMessage = (event: WebSocketEvent) => {

    const handlers = eventHandlers.get(event.event_name)
    if (handlers) {
      handlers.forEach((handler) => handler(event.event_data))
    } else {
    }
  }

  const on = (eventName: string, handler: (data: unknown) => void) => {
    if (!eventHandlers.has(eventName)) {
      eventHandlers.set(eventName, [])
    }
    eventHandlers.get(eventName)?.push(handler)
  }

  const off = (eventName: string, handler: (data: unknown) => void) => {
    const handlers = eventHandlers.get(eventName)
    if (handlers) {
      const index = handlers.indexOf(handler)
      if (index > -1) {
        handlers.splice(index, 1)
      }
    }
  }

  const clearAllHandlers = () => {
    eventHandlers.clear()
  }

  // Volver de una pestaña suspendida no prueba que el socket esté roto: el
  // navegador estrangula los timers, no la conexión. Reconstruir a ciegas anuncia
  // en la UI un corte que el agente nunca sufrió, así que primero preguntamos al
  // backend, que contesta PONG a cualquier PING (whatsapp_router.py).
  const probeConnection = (
    timeoutMs: number = WHATSAPP_CONFIG.CONNECTION_PROBE_TIMEOUT_MS,
  ): Promise<boolean> => {
    if (ws.value?.readyState !== WebSocket.OPEN || !connectionUserId.value) {
      return Promise.resolve(false)
    }

    return new Promise<boolean>((resolve) => {
      let isSettled = false

      const settle = (isAlive: boolean) => {
        if (isSettled) return
        isSettled = true
        window.clearTimeout(timeoutId)
        const index = pendingProbes.indexOf(settle)
        if (index > -1) pendingProbes.splice(index, 1)
        resolve(isAlive)
      }

      const timeoutId = window.setTimeout(() => settle(false), timeoutMs)
      pendingProbes.push(settle)

      // El backend además lo cuenta como PONG del watchdog de sesión.
      send({ event: 'PING', data: { user_id: connectionUserId.value } })
    })
  }

  const manualReconnect = (): Promise<void> => {

    // Resetear intentos y delay
    reconnectAttempts.value = 0
    currentReconnectDelay.value = reconnectDelay
    isReconnecting.value = false

    if (reconnectTimeout.value !== null) {
      clearTimeout(reconnectTimeout.value)
      reconnectTimeout.value = null
    }

    if (!connectionUserId.value) {
      return Promise.reject(new Error('No hay credenciales guardadas para reconectar'))
    }

    // Desconectar primero si hay una conexión existente
    clearHeartbeat()
    const previousSocket = ws.value
    ws.value = null
    isConnected.value = false
    connectivityStore.setWebSocketConnected(false)
    isConnecting.value = false

    // El onclose del socket reemplazado no debe programar una segunda
    // reconexion en paralelo con esta recuperacion inmediata.
    if (previousSocket) {
      previousSocket.onopen = null
      previousSocket.onclose = null
      previousSocket.onerror = null
      previousSocket.onmessage = null
      intentionalDisconnect = true
      previousSocket.close(1000, 'Renovacion de conexion')
    }

    return connect(connectionUserId.value, connectionUserRole.value)
  }

  const emitConnectionStateChange = (state: string, data?: unknown) => {
    const handlers = eventHandlers.get('CONNECTION_STATE_CHANGE')
    if (handlers) {
      handlers.forEach((handler) => handler({ state, data }))
    }
  }

  const onConnectionStateChange = (handler: (data: { state: string; data?: unknown }) => void) => {
    on('CONNECTION_STATE_CHANGE', handler as (data: unknown) => void)
  }

  /**
   * Devuelve si el payload se entrego al socket. `isConnected` es una bandera que
   * se pone en `onclose`, asi que hay una ventana —la red muerta antes de que el
   * navegador lo note— en la que dice `true` con el socket ya no abierto. Sin
   * este booleano el mensaje se perdia en silencio: el envio no lanzaba, y el
   * mensaje temporal se quedaba en el hilo con el reloj de PENDING para siempre.
   */
  const send = (payload: unknown): boolean => {
    if (!ws.value) {
      return false
    }

    if (ws.value.readyState !== WebSocket.OPEN) {
      return false
    }

    try {
      const message = JSON.stringify(payload)
      ws.value.send(message)
      return true
    } catch (error) {
      console.warn('[WhatsApp WS] Error al enviar mensaje:', error, payload)
      return false
    }
  }

  const sendMessage = (payload: SendMessagePayload) => send(payload)

  const enterChat = (payload: EnterChatPayload) => {
    send(payload)
  }

  const exitChat = (payload: ExitChatPayload) => {
    send(payload)
  }

  const endChat = (payload: EndChatPayload) => {
    send(payload)
  }

  const markChatRead = (payload: ReadChatPayload) => {
    send(payload)
  }

  const sendFile = (payload: SendFilePayload) => send(payload)

  const sendInteractionMessage = (payload: InteractionMessagePayload) => {
    send(payload)
  }

  const sendTemplate = (payload: SendTemplatePayload) => {
    send(payload)
  }

  const transferToUser = (payload: TransferUserPayload) => {
    send(payload)
  }

  const transferToCampaign = (payload: {
    type: 'TRANSFER_TO_CAMPAIGN'
    data: {
      chat_id: string
      user_id: string
      user_name: string
      campaign_id: string
      campaign_name: string
    }
  }) => {
    send(payload)
  }

  const transferToBot = (payload: {
    type: 'TRANSFER_TO_BOT'
    data: {
      chat_id: string
      user_id: string
      user_name: string
      bot_id: string
      bot_name: string
      campaign_id: string
      incoming_route_id: string
      phone: string
    }
  }) => {
    send(payload)
  }

  const confirmTransfer = (payload: ConfirmTransferPayload) => {
    send(payload)
  }

  const sendAgentLogout = (userId: string) => {
    send({ type: 'USER_LOGOUT', data: { user_id: userId } })
  }

  const onMessageEvent = (handler: (data: MessageEventData) => void) => {
    on(WebSocketEventName.MESSAGE_EVENT, handler as (data: unknown) => void)
  }

  const onMessageStatusEvent = (handler: (data: MessageStatusEventData) => void) => {
    on(WebSocketEventName.MESSAGE_STATUS_EVENT, handler as (data: unknown) => void)
  }

  const onUserSendTemplateMessage = (handler: (data: UserSendTemplateMessageEvent) => void) => {
    on(WebSocketEventName.USER_SEND_TEMPLATE_MESSAGE, handler as (data: unknown) => void)
  }

  const onNewChat = (handler: (data: NewChatEvent) => void) => {
    on(WebSocketEventName.NEW_CHAT, handler as (data: unknown) => void)
  }

  const onNewChatTemplate = (handler: (data: NewChatTemplateEvent) => void) => {
    on(WebSocketEventName.NEW_CHAT_TEMPLATE, handler as (data: unknown) => void)
  }

  const onUserEntryChat = (handler: (data: UserEntryChatEvent) => void) => {
    on(WebSocketEventName.USER_ENTRY_CHAT, handler as (data: unknown) => void)
  }

  const onUserTookChat = (handler: (data: UserTookChatEvent) => void) => {
    on(WebSocketEventName.USER_TOOK_CHAT, handler as (data: unknown) => void)
  }

  const onUserExitChat = (handler: (data: UserExitChatEvent) => void) => {
    on(WebSocketEventName.USER_EXIT_TO_CHAT, handler as (data: unknown) => void)
  }

  const onInteraction = (handler: (data: InteractionEvent) => void) => {
    on(WebSocketEventName.INTERACTION, handler as (data: unknown) => void)
  }

  const onTransferRequest = (handler: (data: TransferRequestEvent) => void) => {
    on(WebSocketEventName.REQUEST_TRANSFER, handler as (data: unknown) => void)
  }

  const onTransferAccepted = (handler: (data: TransferAcceptedEvent) => void) => {
    on(WebSocketEventName.TRANSFER_ACCEPTED, handler as (data: unknown) => void)
  }

  const onChatTransferredToCampaign = (handler: (data: ChatTransferredToCampaignEvent) => void) => {
    on(WebSocketEventName.CHAT_TRANSFERRED_TO_CAMPAIGN, handler as (data: unknown) => void)
  }

  const onChatTransferredToBot = (handler: (data: ChatTransferredToBotEvent) => void) => {
    on(WebSocketEventName.CHAT_TRANSFERRED_TO_BOT, handler as (data: unknown) => void)
  }

  const onChatFinished = (handler: (data: ChatFinishedEvent) => void) => {
    on(WebSocketEventName.CHAT_FINISHED, handler as (data: unknown) => void)
  }

  const onChatUpdated = (handler: (data: ChatUpdatedEvent) => void) => {
    on(WebSocketEventName.CHAT_UPDATED, handler as (data: unknown) => void)
  }

  onUnmounted(() => {
    disconnect()
  })

  return {
    ws,
    isConnected,
    isReconnecting,
    reconnectAttempts,
    lastCloseCode,
    currentReconnectDelay,
    connect,
    disconnect,
    manualReconnect,
    probeConnection,
    getConnectionState,
    on,
    off,
    clearAllHandlers,
    onConnectionStateChange,
    sendMessage,
    enterChat,
    exitChat,
    endChat,
    markChatRead,
    sendFile,
    sendInteractionMessage,
    sendTemplate,
    transferToUser,
    transferToCampaign,
    transferToBot,
    confirmTransfer,
    sendAgentLogout,
    onMessageEvent,
    onMessageStatusEvent,
    onUserSendTemplateMessage,
    onNewChat,
    onNewChatTemplate,
    onUserEntryChat,
    onUserTookChat,
    onUserExitChat,
    onInteraction,
    onTransferRequest,
    onTransferAccepted,
    onChatTransferredToCampaign,
    onChatTransferredToBot,
    onChatFinished,
    onChatUpdated,
  }
}
